/** Utilidades de periodo. Todas las fechas son strings YYYY-MM-DD (UTC, sin hora). */

export interface Period {
  start: string; // YYYY-MM-DD inclusive
  end: string; // YYYY-MM-DD inclusive
}

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
const MONTH_RE = /^\d{4}-\d{2}$/;

export function assertDate(value: string, field = 'fecha'): string {
  if (!DATE_RE.test(value)) {
    throw new Error(`La ${field} "${value}" no tiene formato YYYY-MM-DD.`);
  }
  return value;
}

export function monthOf(date: string): string {
  return assertDate(date).slice(0, 7);
}

export function inPeriod(date: string, period: Period): boolean {
  return date >= period.start && date <= period.end;
}

export function daysInPeriod(period: Period): number {
  const a = Date.parse(`${period.start}T00:00:00Z`);
  const b = Date.parse(`${period.end}T00:00:00Z`);
  return Math.floor((b - a) / 86_400_000) + 1;
}

/** Lista de meses YYYY-MM cubiertos por el periodo, en orden. */
export function monthsIn(period: Period): string[] {
  const out: string[] = [];
  let [y, m] = period.start.split('-').map(Number);
  const endMonth = monthOf(period.end);
  for (let guard = 0; guard < 600; guard++) {
    const key = `${y}-${String(m).padStart(2, '0')}`;
    out.push(key);
    if (key >= endMonth) break;
    m += 1;
    if (m > 12) {
      m = 1;
      y += 1;
    }
  }
  return out;
}

export function addMonths(month: string, delta: number): string {
  if (!MONTH_RE.test(month)) throw new Error(`Mes invalido: "${month}"`);
  const [y, m] = month.split('-').map(Number);
  const total = y * 12 + (m - 1) + delta;
  const ny = Math.floor(total / 12);
  const nm = (total % 12) + 1;
  return `${ny}-${String(nm).padStart(2, '0')}`;
}

/** Cuantos meses hay de `from` (inclusive) a `to` (exclusive). */
export function monthDiff(from: string, to: string): number {
  const [y1, m1] = from.split('-').map(Number);
  const [y2, m2] = to.split('-').map(Number);
  return (y2 - y1) * 12 + (m2 - m1);
}

export function periodOfMonth(month: string): Period {
  const [y, m] = month.split('-').map(Number);
  const lastDay = new Date(Date.UTC(y, m, 0)).getUTCDate();
  return {
    start: `${month}-01`,
    end: `${month}-${String(lastDay).padStart(2, '0')}`,
  };
}

export function currentMonth(today = new Date()): string {
  return today.toISOString().slice(0, 7);
}

export function yearToDate(today = new Date()): Period {
  const iso = today.toISOString().slice(0, 10);
  return { start: `${iso.slice(0, 4)}-01-01`, end: iso };
}
